import { useEffect, useRef, useState } from "react";
import { Box, Image, Skeleton, Spinner, Stack, Text } from "@chakra-ui/react"; // Assuming Chakra UI for Image component
import { getMediaURL } from "utils/misc";

// Module-level cache: same imei::type::src reuses a single in-flight / resolved
// Promise so remounts and duplicate renders don't trigger repeat network calls.
const mediaUrlCache = new Map();

const PhotosGalleryImageComponent = ({ src, onMouseEnter, height = 150, type = "thumbnail", isPhotoGallery, ...props }) => {
  const [srcURL, setSrcURL] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const lastFetchedSrc = useRef(null);

  useEffect(() => {
    if (!src || lastFetchedSrc.current === src) return;
    lastFetchedSrc.current = src;
    const cacheKey = `${props.imei}::${type}::${src}`;
    let cached = mediaUrlCache.get(cacheKey);
    if (!cached) {
      cached = getMediaURL(src, props.imei, type);
      mediaUrlCache.set(cacheKey, cached);
    }
    cached
      .then((res) => {
        setSrcURL(res?.data?.data);
        setImageLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching media URL:", error);
        mediaUrlCache.delete(cacheKey);
        setImageLoading(false);
        setSrcURL(undefined);
      });
  }, [src]);

  const containerProps = {
    cursor: "pointer",
    onMouseEnter,
    /* _hover: {
      transform: "scale(1.05)",
      transition: "0.2s",
    }, */
    width: props.width || "100%",
    height,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    style: {
      objectFit: props.objectFit || "contain",
    },
    ...props,
  };

  const isVideo = (src) => {
    return src?.indexOf(".mp4") > -1;
  };

  if (imageLoading) {
    if (type === "resized" && isPhotoGallery) {
      return (
        <Box
          width={{ base: "85vw", md: "60vw" }}
          height={{ base: "50vh", md: "60vh" }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          bg="rgba(255, 255, 255, 0.04)"
          borderRadius="8px"
          backdropFilter="blur(4px)"
          gap="14px"
        >
          <Spinner
            thickness="3px"
            speed="0.75s"
            emptyColor="rgba(255,255,255,0.15)"
            color="#3fd0ff"
            size="xl"
          />
          <Text color="rgba(255,255,255,0.85)" fontSize="14px" letterSpacing="0.3px">
            Loading…
          </Text>
        </Box>
      );
    }
    return (
      <Stack gap="6" maxW="xs">
      <Skeleton height="200px" />
    </Stack>
    );
  }

  if (isVideo(src)) {
    return (
      <Box {...containerProps}>
        <video 
          controls 
          controlsList="nodownload"
          preload={"auto"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain"
          }}
        >
          <source src={srcURL} type="video/mp4" />
        </video>
      </Box>
    );
  }

  return <Image src={srcURL} {...containerProps} />;
};

export default PhotosGalleryImageComponent;
